import { Box, Divider, Menu, TextField, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../../../api/hooks/useIsIntegrated";
import { notificationSettingsKeys } from "../../../../../../api/queryKeys";
import TextButton from "../../../../../../components/buttons/TextButton";
import LoadingIcon from "../../../../../../components/LoadingIcon";
import { Toast } from "../../../../../../components/Toast";
import IntegrationImage from "../../../../../stack/integrations/components/IntegrationImage";
import { IntegrationsMapping } from "../../../../../stack/integrations/IntegrationsMapping";
import { notificationMethod } from "../../../../constants";
import { field_IDs_mapping } from "../../../fieldMapping";

export default function DriftPercentageMenu({
  menuAnchorEl,
  setMenuAnchorEl,
  initialDriftByTool,
  email,
}) {
  const colors = useColors();
  const queryClient = useQueryClient();
  const { integrations } = useIsIntegrated();
  const [driftByTool, setDriftByTool] = useState(initialDriftByTool);
  const { getIntegrationsInfo, updateNotificationSetting } = useAPI();

  const onDriftChange = (tool, percentage) => {
    setDriftByTool((prev) => ({
      ...prev,
      [tool]: percentage,
    }));
  };

  const integrationsInfo = useQuery({
    queryKey: ["integrationsInfo"],
    queryFn: getIntegrationsInfo,
    select: (response) => response.data,
  });

  const driftSaveMutation = useMutation({
    mutationFn: () =>
      updateNotificationSetting(
        notificationMethod.EMAIL,
        field_IDs_mapping.INVENTORY.ENTITIES_DRIFT,
        {
          email: email,
          drift_percentage_by_tool: driftByTool,
        }
      ),

    onSuccess: () => {
      Toast.success("Inventory drift percentage saved successfully");
      setMenuAnchorEl(null);
      queryClient.invalidateQueries({
        queryKey: notificationSettingsKeys.all,
      });
    },
    onError: () => {
      Toast.error("Error saving inventory drift percentage");
    },
  });

  const toolsWithCollectors = integrationsInfo.isSuccess
    ? Object.keys(integrationsInfo.data).filter(
        (tool) =>
          integrationsInfo.data[tool].collectors &&
          integrationsInfo.data[tool].collectors.length > 0 &&
          integrations.includes(tool)
      )
    : [];

  return (
    <Menu
      id="simple-menu"
      anchorEl={menuAnchorEl}
      open={Boolean(menuAnchorEl)}
      onClose={() => {
        setMenuAnchorEl(null);
      }}
      anchorOrigin={{
        horizontal: "left",
      }}
      transformOrigin={{
        horizontal: -65,
      }}
      sx={{
        overflow: "hidden",
        ".MuiPaper-root": {
          borderRadius: 2,
          boxShadow: "0px 2px 5px 1px " + colors.grey_500,
          overflow: "hidden",
        },
      }}
    >
      {integrationsInfo.isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="300px"
          height="300px"
        >
          <LoadingIcon size={8} />
        </Box>
      ) : integrationsInfo.isSuccess ? (
        <Box
          display="flex"
          flexDirection="column"
          gap={1}
          padding="0 8px"
          justifyContent="space-between"
          overflow="hidden"
        >
          <Box maxHeight="250px" overflow="auto" paddingRight={1}>
            <Box display="flex" flexDirection="column" gap={1} padding="6px 0">
              {toolsWithCollectors.map((integration) => (
                <Box
                  key={integration}
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={3}
                >
                  <Box
                    display="flex"
                    flexDirection="row"
                    gap={1}
                    alignItems="center"
                  >
                    <IntegrationImage
                      name={integration}
                      width="16px"
                      newLogo={true}
                    />
                    <Typography variant="caption">
                      {IntegrationsMapping[integration].displayName}
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="row"
                    gap={1}
                    alignItems="center"
                  >
                    <TextField
                      id="inventory-drift"
                      value={driftByTool?.[integration] || ""}
                      fullWidth
                      onChange={(event) => {
                        onDriftChange(integration, event.target.value);
                      }}
                      type="number"
                      sx={{
                        width: "70px",
                        height: "20px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "6px",
                          "& .MuiInputBase-input": {
                            height: "25px",
                            padding: "0 6px 0 14px",
                          },
                        },
                        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: colors.grey_400,
                            borderWidth: "1px",
                          },
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: colors.grey_400,
                            borderWidth: "1px",
                          },
                      }}
                      InputProps={{
                        style: {
                          fontSize: "12px",
                        },
                      }}
                    />
                    <Typography variant="caption">%</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            gap={1}
            sx={{ backgroundColor: colors.common_white }}
            width="100%"
          >
            <Divider />
            <Box display="flex" justifyContent="center">
              <TextButton
                color="primary"
                size="small"
                onClick={() => {
                  driftSaveMutation.mutate();
                }}
                loading={driftSaveMutation.isPending}
                disabled={
                  JSON.stringify(driftByTool) ===
                  JSON.stringify(initialDriftByTool)
                }
              >
                Save
              </TextButton>
            </Box>
          </Box>
        </Box>
      ) : null}
    </Menu>
  );
}
